import classNames from 'classnames';
import type { Bounds } from 'quill/core';
import React, { useEffect, useState } from 'react';
import type { MagneticWallType } from './editorSlice';
import { useQuill } from './QuillContext';
import { useRangeSaver } from './RangeSaver';
import * as styles from './TargetHighlight.module.scss';
import { useUIElementResizeListener } from './UIElementResizeContext';
import getRangeBoundaries from './utils/getRangeBoundaries';

const useRangeBoundaries = () => {
  const { quill } = useQuill();
  const range = useRangeSaver(quill);
  const [boundaries, setBoundaries] = useState<Bounds[] | null>(null);
  const addUIElementResizeListener = useUIElementResizeListener();

  useEffect(() => {
    const update = () => {
      setBoundaries(quill && range && getRangeBoundaries(quill, range));
    };

    const removeUIElementResizeListener = addUIElementResizeListener(update);
    update();
    return removeUIElementResizeListener;
  }, [quill, range, addUIElementResizeListener]);

  return boundaries;
};

const highlightClassNames: Record<MagneticWallType, string> = {
  comment: styles.comment,
  reaction: styles.comment,
  annotation: styles.annotation,
};

interface TargetHighlightProps {
  type: MagneticWallType;
}

const TargetHighlight = ({ type }: TargetHighlightProps) => {
  const boundaries = useRangeBoundaries();
  return boundaries ? (
    <>
      {boundaries.map((bounds, i) => (
        <span
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          className={classNames(highlightClassNames[type], styles.selection)}
          style={{
            left: bounds.left,
            height: bounds.height,
            top: bounds.top,
            width: bounds.width,
          }}
        />
      ))}
    </>
  ) : null;
};

export default TargetHighlight;
